/**
 * @file The people or team rows of the Overview tables: one row per key of
 * `group` found in a `salesClosedBy…` answer or in a `quoteValueBy…` answer,
 * with its booked and its quoted (Quote and Buy together). Consumers: the
 * Overview person and team tables. Booked is the sales credit
 * (`creditAmount`) from the `salesClosedBy…` answer at every level, never the
 * whole `amount`; a key with quotes only books `"0"`; rows sort by booked,
 * largest first, then by key with the null key last. A key cell that is
 * neither text nor null fails the whole result, and a quoted answer that does
 * not answer marks every row's quoted with its state, never zero.
 */
import type { Snapshot } from 'valtio';

import { analyticsAnswerFigure } from './analyticsAnswerFigure';
import { analyticsDecimalCompare } from './analyticsDecimalCompare';
import { analyticsUnansweredFigure } from './analyticsUnansweredFigure';
import type {
  AnalyticsGroupColumn,
  AnalyticsGroupRow,
} from './analyticsOverviewTab.types';
import type { AnalyticsAnswer, AnalyticsUnansweredFigure } from './index.types';

const byKey = (a: string | null, b: string | null): number =>
  a === b ? 0 : a === null ? 1 : b === null ? -1 : a < b ? -1 : 1;

export const analyticsGroupRows = (
  booked: Snapshot<AnalyticsAnswer> | undefined,
  quoted: Snapshot<AnalyticsAnswer> | undefined,
  group: AnalyticsGroupColumn,
):
  | { status: 'answered'; rows: AnalyticsGroupRow[] }
  | AnalyticsUnansweredFigure => {
  if (booked?.status !== 'answered') return analyticsUnansweredFigure(booked);
  const bookedTotal = analyticsAnswerFigure(booked, 'creditAmount');
  if (bookedTotal.status !== 'answered') return bookedTotal;
  const sources = [
    { query: booked.result.query, rows: booked.result.rows },
    ...(quoted?.status === 'answered'
      ? [{ query: quoted.result.query, rows: quoted.result.rows }]
      : []),
  ];
  const malformed = sources.find(({ rows }) =>
    rows.some((row) => typeof row[group] !== 'string' && row[group] !== null),
  );
  if (malformed !== undefined)
    return {
      status: 'refused',
      answer: {
        status: 'failed',
        message: `The ${malformed.query} answer holds no text ${group}.`,
      },
    };
  const keys = [
    ...new Set(
      sources.flatMap(({ rows }) =>
        rows.flatMap((row) => {
          const key = row[group];
          return typeof key === 'string' || key === null ? [key] : [];
        }),
      ),
    ),
  ];
  const figures = keys.map((key) => ({
    key,
    booked: analyticsAnswerFigure(
      booked,
      'creditAmount',
      (row) => row[group] === key,
    ),
    quoted: analyticsAnswerFigure(
      quoted,
      'quotedAmount',
      (row) => row[group] === key,
    ),
  }));
  return {
    status: 'answered',
    rows: figures
      .flatMap(({ key, booked: bookedFigure, quoted: quotedFigure }) =>
        bookedFigure.status === 'answered'
          ? [{ key, booked: bookedFigure.decimal, quoted: quotedFigure }]
          : [],
      )
      .sort(
        (a, b) =>
          analyticsDecimalCompare(b.booked, a.booked) || byKey(a.key, b.key),
      ),
  };
};
